import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { FormattedMessage } from '@futrli/localisation';
import { UILoader } from '@futrli/ui-components';
import { SheetsImportInfo, SheetsImportInfoResult } from '@futrli-sdk/predict';

import { createImportSheet } from '../../../../../../../core/api/external-sheets';
import { getImportSheetInfo } from '../../../../../../../core/api/external-sheets';
import { RedirectRoute } from '../../../redirect-route';
import { RedirectToImport } from '../../components/redirect-to-import/redirect-to-import';
import { TRACKING_EVENTS } from '../../../../../../../core/constants';

import styles from './import-redirect.scss';

/**
 * Route to automatically create an import master sheet for a new user while showing a spinner.
 * Send user back to the Import page if they've just re-authenticated with Google
 */
export const ImportRedirect: React.FC = (): JSX.Element => {
  const { organisationId: tempOrganisationId, workspaceId: tempWorkspaceId, forecastId } = useParams();
  const organisationId = tempOrganisationId as string;
  const workspaceId = tempWorkspaceId as string;
  const [sheetInfo, setSheetInfo] = useState<SheetsImportInfo | null>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // fetch import sheet info to determine where to send user
  useEffect(() => {
    async function getSheetInfo() {
      setIsLoading(true);
      let sheetInfoResult: SheetsImportInfoResult | undefined;
      try {
        sheetInfoResult = await getImportSheetInfo(workspaceId, organisationId, forecastId as string);
      } catch {
        setSheetInfo(null);
        return;
      } finally {
        setIsLoading(false);
      }

      if (sheetInfoResult && !sheetInfoResult.error) {
        setSheetInfo(sheetInfoResult.import_sheet_info);
      } else {
        setSheetInfo(null);
      }
    }

    // load sheet info
    getSheetInfo();
  }, [workspaceId, organisationId, forecastId]);

  // show loading spinner if info is loading
  if (isLoading || typeof sheetInfo === 'undefined') {
    return (
      <section data-testid="import-page-loading" className={styles.loading}>
        <UILoader />
      </section>
    );
  }

  // if user already has a sheet, they've re-autheticated
  // so send them back to the import page
  if (sheetInfo?.sheet_exists) {
    return <RedirectToImport />;
  } else {
    // if user doesn't have a sheet, they've authenticated for the first time
    // create a master sheet
    return (
      <RedirectRoute
        createSheet={createImportSheet}
        loadingText={
          <FormattedMessage id="import.create_in_progress" defaultMessage="Creating your import master file" />
        }
        trackingEvent={TRACKING_EVENTS.IMPORT_CREATE}
      />
    );
  }
};
